import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./InfoIcon.scss";

/**
 * Enum-like placement of the info icon
 */
const INFO_ICON_PLACEMENT = Object.freeze({
  RIGHT: "right",
  LEFT: "left",
  TOP: "top",
  BOTTOM: "bottom",
});

const DEFAULT_OFFSET = 8;
const VIEWPORT_PADDING = 8;
const TOOLTIP_Z_INDEX = 10000;
const POSITION_FIXED = "fixed";

/**
 * InfoIcon
 * Props:
 *  - text: string (tooltip content)
 *  - placement?: one of INFO_ICON_PLACEMENT values  (default: INFO_ICON_PLACEMENT.RIGHT)
 *  - flip?: boolean                        (default: true)
 *  - offset?: number                       (default: 8)
 */
const InfoIcon = ({
  text = "",
  placement = INFO_ICON_PLACEMENT.RIGHT,
  flip = true,
  offset = DEFAULT_OFFSET,
}) => {
  const hasText =
    typeof text === "string" ? text.trim().length > 0 : Boolean(text);

  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [viewportPosition, setViewportPosition] = useState({ top: 0, left: 0 });
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);

  /** Returns a candidate order based on preferred placement. */
  function getPlacementOrder(preferredPlacement) {
    switch (preferredPlacement) {
      case INFO_ICON_PLACEMENT.LEFT: {
        return [INFO_ICON_PLACEMENT.LEFT, INFO_ICON_PLACEMENT.RIGHT, INFO_ICON_PLACEMENT.TOP, INFO_ICON_PLACEMENT.BOTTOM];
      }
      case INFO_ICON_PLACEMENT.TOP: {
        return [INFO_ICON_PLACEMENT.TOP, INFO_ICON_PLACEMENT.BOTTOM, INFO_ICON_PLACEMENT.RIGHT, INFO_ICON_PLACEMENT.LEFT];
      }
      case INFO_ICON_PLACEMENT.BOTTOM: {
        return [INFO_ICON_PLACEMENT.BOTTOM, INFO_ICON_PLACEMENT.TOP, INFO_ICON_PLACEMENT.RIGHT, INFO_ICON_PLACEMENT.LEFT];
      }
      case INFO_ICON_PLACEMENT.RIGHT:
      default: {
        return [INFO_ICON_PLACEMENT.RIGHT, INFO_ICON_PLACEMENT.LEFT, INFO_ICON_PLACEMENT.TOP, INFO_ICON_PLACEMENT.BOTTOM];
      }
    }
  }

  /**
   * Compute top/left for a given side relative to the anchor and tooltip rects.
   */
  function computePosition(side, anchorRect, tooltipRect, gap) {
    let left = 0;
    let top = 0;

    if (side === INFO_ICON_PLACEMENT.RIGHT) {
      left = anchorRect.right + gap;
      top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;
    }
    else if (side === INFO_ICON_PLACEMENT.LEFT) {
      left = anchorRect.left - gap - tooltipRect.width;
      top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;
    }
    else if (side === INFO_ICON_PLACEMENT.TOP) {
      left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      top = anchorRect.top - gap - tooltipRect.height;
    }
    else {
      left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      top = anchorRect.bottom + gap;
    }

    return { left, top };
  }

  /** Check if tooltip fully fits in viewport within padding. */
  function isFullyVisible(position, tooltipRect, viewportWidth, viewportHeight, viewportPadding) {
    if (position.left < viewportPadding) {
      return false;
    }
    if (position.top < viewportPadding) {
      return false;
    }
    if (position.left + tooltipRect.width > viewportWidth - viewportPadding) {
      return false;
    }
    if (position.top + tooltipRect.height > viewportHeight - viewportPadding) {
      return false;
    }
    return true;
  }

  /** Clamp tooltip position within viewport as a fallback. */
  function clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPadding) {
    const clampedLeft = Math.max(
      viewportPadding,
      Math.min(viewportWidth - tooltipRect.width - viewportPadding, position.left)
    );
    const clampedTop = Math.max(
      viewportPadding,
      Math.min(viewportHeight - tooltipRect.height - viewportPadding, position.top)
    );
    return { left: clampedLeft, top: clampedTop };
  }

  /** Measure & compute final tooltip position and placement. */
  const positionTooltip = () => {
    const anchorEl = anchorRef.current;
    const tooltipEl = tooltipRef.current;

    if (!anchorEl || !tooltipEl) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportPadding = VIEWPORT_PADDING;

    const anchorRect = anchorEl.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    const candidateOrder = getPlacementOrder(placement);

    let chosenPlacement = placement;
    let position = computePosition(placement, anchorRect, tooltipRect, offset);

    if (flip) {
      let foundFit = false;
      for (const side of candidateOrder) {
        const candidatePos = computePosition(side, anchorRect, tooltipRect, offset);
        if (isFullyVisible(candidatePos, tooltipRect, viewportWidth, viewportHeight, viewportPadding)) {
          position = candidatePos;
          chosenPlacement = side;
          foundFit = true;
          break;
        }
      }
      if (!foundFit) {
        // Clamp preferred placement if none fully fit.
        position = clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPadding);
        chosenPlacement = placement;
      }
    } else {
      // No flip: clamp preferred placement.
      position = clampToViewport(position, tooltipRect, viewportWidth, viewportHeight, viewportPadding);
      chosenPlacement = placement;
    }

    setViewportPosition({ top: position.top, left: position.left });
    setResolvedPlacement(chosenPlacement);
  };

  // Position after paint when opening or when inputs change.
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }
    const rafId = requestAnimationFrame(positionTooltip);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, text, placement, flip, offset]);

  // Reposition on scroll/resize while open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onScroll = () => requestAnimationFrame(positionTooltip);
    const onResize = () => requestAnimationFrame(positionTooltip);

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!hasText) {
    return null;
  }

  const tooltipNode = (
    <div
      ref={tooltipRef}
      role="tooltip"
      className={`info-icon-text info-icon-text--${resolvedPlacement}`}
      style={{
        position: POSITION_FIXED,
        top: viewportPosition.top,
        left: viewportPosition.left,
        zIndex: TOOLTIP_Z_INDEX,
      }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {text}
    </div>
  );

  return (
    <span
      className="info-icon"
      ref={anchorRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      tabIndex={0}
      aria-haspopup="dialog"
    >
      <FontAwesomeIcon icon={faCircleInfo} />
      {isOpen && typeof document !== "undefined" && createPortal(tooltipNode, document.body)}
    </span>
  );
};

export { INFO_ICON_PLACEMENT, InfoIcon as default };